/**
 * Property-expression engine — Detect's AppliesTo equivalent (PRD §2.4).
 * Evaluates targeting expressions against a resource property map:
 *
 *   system.os == "windows" && custom.env != "lab"
 *   hasProperty(system.snmp.sysobjectid) || system.kind == "website"
 *   system.ports contains "443"
 *
 * Grammar (deliberately small, no user-supplied code execution):
 *   expr    := orExpr
 *   orExpr  := andExpr ( '||' andExpr )*
 *   andExpr := unary ( '&&' unary )*
 *   unary   := '!' unary | '(' expr ')' | comparison
 *   comparison := operand ( ('=='|'!='|'contains'|'startsWith'|'=~') operand )?
 *   operand := propertyRef | stringLiteral
 *   propertyRef := ident ( '.' ident )*
 *
 * Bare refs and hasProperty() are deliberately DIFFERENT:
 *   - a bare ref is TRUTHINESS: property exists and is not ""/"false"/"0";
 *   - hasProperty(ref) is EXISTENCE: the key is present, whatever its value.
 *
 * Comparisons are case-insensitive on values (LM convention). A missing
 * property compares as the empty string and is falsy as a bare reference.
 * `=~` is a regex match; patterns compile with a length cap as a ReDoS
 * guard — invalid patterns evaluate to false rather than throwing.
 */
const OPERATORS = ['==', '!=', '&&', '||'];
const WORD_OPERATORS = ['contains', 'startswith'];
const MAX_EXPRESSION_LENGTH = 2048;
const MAX_REGEX_LENGTH = 256;
export class ExpressionError extends Error {
    position;
    constructor(message, position) {
        super(`${message} (at position ${position})`);
        this.position = position;
        this.name = 'ExpressionError';
    }
}
function tokenize(input) {
    const tokens = [];
    let i = 0;
    while (i < input.length) {
        const ch = input[i];
        if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
            i++;
            continue;
        }
        if (ch === '(') {
            tokens.push({ kind: 'lparen', value: '(', pos: i });
            i++;
            continue;
        }
        if (ch === ')') {
            tokens.push({ kind: 'rparen', value: ')', pos: i });
            i++;
            continue;
        }
        const two = input.slice(i, i + 2);
        if (OPERATORS.includes(two)) {
            tokens.push({ kind: 'op', value: two, pos: i });
            i += 2;
            continue;
        }
        if (two === '=~') {
            tokens.push({ kind: 'op', value: '=~', pos: i });
            i += 2;
            continue;
        }
        if (ch === '!') {
            tokens.push({ kind: 'bang', value: '!', pos: i });
            i++;
            continue;
        }
        if (ch === '"' || ch === "'") {
            const quote = ch;
            let j = i + 1;
            let out = '';
            while (j < input.length && input[j] !== quote) {
                if (input[j] === '\\' && j + 1 < input.length) {
                    const next = input[j + 1];
                    // Only quote characters and the backslash itself are escapable.
                    // Everything else keeps its backslash so regex escapes like \d
                    // or \. survive into the pattern text unmutated.
                    if (next === quote || next === '\\') {
                        out += next;
                    }
                    else {
                        out += '\\' + next;
                    }
                    j += 2;
                }
                else {
                    out += input[j];
                    j++;
                }
            }
            if (j >= input.length) {
                throw new ExpressionError('unterminated string literal', i);
            }
            tokens.push({ kind: 'string', value: out, pos: i });
            i = j + 1;
            continue;
        }
        if (/[A-Za-z_]/.test(ch)) {
            let j = i;
            while (j < input.length && /[A-Za-z0-9_.-]/.test(input[j]))
                j++;
            const word = input.slice(i, j);
            const lower = word.toLowerCase();
            if (WORD_OPERATORS.includes(lower)) {
                tokens.push({ kind: 'op', value: lower, pos: i });
            }
            else {
                tokens.push({ kind: 'ident', value: word, pos: i });
            }
            i = j;
            continue;
        }
        throw new ExpressionError(`unexpected character "${ch}"`, i);
    }
    return tokens;
}
class Parser {
    tokens;
    properties;
    pos = 0;
    constructor(tokens, properties) {
        this.tokens = tokens;
        this.properties = properties;
    }
    parse() {
        const result = this.orExpr();
        if (this.pos < this.tokens.length) {
            throw new ExpressionError(`unexpected token "${this.tokens[this.pos].value}"`, this.tokens[this.pos].pos);
        }
        return result;
    }
    peek() {
        return this.tokens[this.pos];
    }
    orExpr() {
        let left = this.andExpr();
        while (this.peek()?.kind === 'op' && this.peek()?.value === '||') {
            this.pos++;
            const right = this.andExpr();
            left = left || right;
        }
        return left;
    }
    andExpr() {
        let left = this.unary();
        while (this.peek()?.kind === 'op' && this.peek()?.value === '&&') {
            this.pos++;
            const right = this.unary();
            left = left && right;
        }
        return left;
    }
    unary() {
        const tok = this.peek();
        if (!tok) {
            throw new ExpressionError('unexpected end of expression', -1);
        }
        if (tok.kind === 'bang') {
            this.pos++;
            return !this.unary();
        }
        if (tok.kind === 'lparen') {
            this.pos++;
            const inner = this.orExpr();
            const close = this.peek();
            if (!close || close.kind !== 'rparen') {
                throw new ExpressionError('expected ")"', tok.pos);
            }
            this.pos++;
            return inner;
        }
        return this.comparison();
    }
    comparison() {
        const left = this.operand();
        const opTok = this.peek();
        if (opTok &&
            opTok.kind === 'op' &&
            ['==', '!=', 'contains', 'startswith', '=~'].includes(opTok.value)) {
            this.pos++;
            const right = this.operand();
            const l = left.value.toLowerCase();
            const r = right.value.toLowerCase();
            switch (opTok.value) {
                case '==':
                    return l === r;
                case '!=':
                    return l !== r;
                case 'contains':
                    return l.includes(r);
                case 'startswith':
                    return l.startsWith(r);
                case '=~': {
                    if (right.value.length > MAX_REGEX_LENGTH)
                        return false;
                    try {
                        return new RegExp(right.value, 'i').test(left.value);
                    }
                    catch {
                        return false;
                    }
                }
                /* istanbul ignore next -- exhaustive */
                default:
                    throw new ExpressionError(`unknown operator "${opTok.value}"`, opTok.pos);
            }
        }
        // Bare reference: truthy when the property exists and is not
        // empty/"false"/"0".
        if (left.isRef) {
            return left.value !== '' && left.value !== 'false' && left.value !== '0';
        }
        throw new ExpressionError('string literal is not a valid boolean expression', -1);
    }
    operand() {
        const tok = this.peek();
        if (!tok) {
            throw new ExpressionError('expected operand, got end of expression', -1);
        }
        if (tok.kind === 'string') {
            this.pos++;
            return { value: tok.value, isRef: false };
        }
        if (tok.kind === 'ident') {
            this.pos++;
            // hasProperty(ref) sugar
            if (tok.value.toLowerCase() === 'hasproperty' && this.peek()?.kind === 'lparen') {
                this.pos++;
                const ref = this.peek();
                if (!ref || ref.kind !== 'ident') {
                    throw new ExpressionError('hasProperty() expects a property reference', tok.pos);
                }
                this.pos++;
                const close = this.peek();
                if (!close || close.kind !== 'rparen') {
                    throw new ExpressionError('expected ")" after hasProperty argument', tok.pos);
                }
                this.pos++;
                const exists = Object.prototype.hasOwnProperty.call(this.properties, ref.value.toLowerCase());
                return { value: exists ? 'true' : '', isRef: true };
            }
            const value = this.lookup(tok.value);
            return { value, isRef: true };
        }
        throw new ExpressionError(`expected operand, got "${tok.value}"`, tok.pos);
    }
    lookup(ref) {
        const key = ref.toLowerCase();
        if (Object.prototype.hasOwnProperty.call(this.properties, key)) {
            return this.properties[key];
        }
        return '';
    }
}
/**
 * Evaluate a property expression against a property map. Property keys
 * are matched case-insensitively (the map is lowercased once here).
 *
 * @throws ExpressionError on syntax errors — callers validate expressions
 *         at write time so runtime evaluation over stored expressions
 *         should not throw; the targeting engine treats a throw as
 *         "does not match" and logs it.
 */
export function evaluatePropertyExpression(expression, properties) {
    if (expression.length > MAX_EXPRESSION_LENGTH) {
        throw new ExpressionError('expression too long', MAX_EXPRESSION_LENGTH);
    }
    const normalized = {};
    for (const [k, v] of Object.entries(properties)) {
        normalized[k.toLowerCase()] = v;
    }
    const tokens = tokenize(expression);
    if (tokens.length === 0) {
        throw new ExpressionError('empty expression', 0);
    }
    return new Parser(tokens, normalized).parse();
}
/** Validate syntax without evaluating (used at sensor/rule write time). */
export function validatePropertyExpression(expression) {
    try {
        evaluatePropertyExpression(expression, {});
        return { valid: true };
    }
    catch (err) {
        if (err instanceof ExpressionError) {
            return { valid: false, error: err.message };
        }
        throw err;
    }
}
//# sourceMappingURL=property-expression.js.map